import React, { useEffect, useState } from "react";
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  useMediaQuery,
  Grid,
  Popover,
  Box,
  Snackbar,
  Alert,
  Slide,
  Typography,
  Card,
  Stack,
  Divider,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import { SketchPicker } from "react-color";
import { useSettings } from "context/SettingsContext";
import AnseraLogo from "assets/images/Anser.png";

const StyledInput = ({
  type = "text",
  value,
  onChange,
  name,
  placeholder,
  startAdornment,
  endAdornment,
}) => {
  return (
    <SoftBox
      sx={{
        position: "relative",
        width: "100%",
      }}
    >
      {startAdornment && (
        <SoftBox
          sx={{
            position: "absolute",
            left: "12px",
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 1,
            color: "text.secondary",
          }}
        >
          {startAdornment}
        </SoftBox>
      )}
      <input
        type={type}
        name={name}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        style={{
          width: "100%",
          padding: "12px 16px",
          paddingLeft: startAdornment ? "40px" : "16px",
          paddingRight: endAdornment ? "40px" : "16px",
          borderRadius: "8px",
          border: "1px solid #d2d6da",
          backgroundColor: "#ffffff",
          fontSize: "0.875rem",
          fontWeight: 400,
          outline: "none",
          transition: "all 0.2s ease-in-out",
          boxSizing: "border-box",
          color: "#344767",
        }}
        onFocus={(e) => {
          e.target.style.borderColor = "#219297";
          e.target.style.boxShadow = "0 0 0 2px rgba(33, 146, 151, 0.1)";
          e.target.style.backgroundColor = "#f8f9fa";
        }}
        onBlur={(e) => {
          e.target.style.borderColor = "#d2d6da";
          e.target.style.boxShadow = "none";
          e.target.style.backgroundColor = "#ffffff";
        }}
      />
      {endAdornment && (
        <SoftBox
          sx={{
            position: "absolute",
            right: "12px",
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 1,
            color: "text.secondary",
            fontSize: "0.75rem",
            fontWeight: 600,
          }}
        >
          {endAdornment}
        </SoftBox>
      )}
    </SoftBox>
  );
};

const LoadingState = () => (
  <SoftBox textAlign="center" py={6}>
    <SoftTypography variant="h5" fontWeight="bold" gutterBottom color="text">
      Appearance tab will appear once the data sync is completed...
    </SoftTypography>
    <SoftTypography variant="body2" color="text.secondary" gutterBottom>
      Please wait while we load your settings
    </SoftTypography>
    <SoftBox mt={3} display="flex" justifyContent="center" gap={1}>
      {[0, 1, 2].map((index) => (
        <SoftBox
          key={index}
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: "#219197",
            animation: "pulse 1.4s infinite ease-in-out",
            animationDelay: `${index * 0.2}s`,
          }}
        />
      ))}
    </SoftBox>
    <style>
      {`
        @keyframes pulse {
          0%, 80%, 100% {
            transform: scale(0.8);
            opacity: 0.6;
          }
          40% {
            transform: scale(1.2);
            opacity: 1;
          }
        }
      `}
    </style>
  </SoftBox>
);

const RefreshingLoader = ({ message = "Refreshing settings..." }) => (
  <SoftBox 
    sx={{ 
      textAlign: "center", 
      py: 4,
      backgroundColor: "#f8f9fa",
      borderRadius: "12px",
      my: 2
    }}
  >
    <CircularProgress 
      size={40} 
      sx={{ 
        color: "#219297",
        mb: 2
      }} 
    />
    <SoftTypography variant="body2" color="text.secondary">
      {message}
    </SoftTypography>
  </SoftBox>
);

const NotificationBanner = ({ show, onClose }) => {
  if (!show) return null;

  return (
    <Card
      sx={{
        backgroundColor: "white",
        border: "1px solid",
        borderColor: "teal",
        borderRadius: "12px",
        p: 2,
        mb: 3,
        boxShadow: "0 4px 12px rgba(165, 210, 212, 1)",
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} alignItems="center" justifyContent="space-between" spacing={1}>
        <SoftTypography variant="body2" color="primary.main" fontWeight="medium" sx={{ flex: 1 }}>
          After installing Ansera app, please publish your website to add AI Chat Assistant Widget
        </SoftTypography>
        <SoftButton
          variant="text"
          color="teal"
          size="small"
          circular
          iconOnly
          onClick={onClose}
          sx={{ minWidth: "auto", p: 0.5 }}
        >
          ✕
        </SoftButton>
      </Stack>
    </Card>
  );
};

const SectionHeader = ({ title, description, icon }) => (
  <SoftBox mb={1}>
    <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
      {icon}
      <SoftTypography variant="h6" fontWeight="bold" color="text">
        {title}
      </SoftTypography>
    </Stack>
    {description && (
      <SoftTypography variant="body2" color="text.secondary">
        {description}
      </SoftTypography>
    )}
  </SoftBox>
);

const SettingsRow = ({ label, element, isMobile, labelWidth = "220px" }) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    alignItems={{ xs: "flex-start", sm: "center" }}
    spacing={2}
    sx={{ py: 2, borderBottom: "1px solid", borderColor: "divider" }}
  >
    <SoftBox width={{ xs: "100%", sm: labelWidth }} minWidth={{ xs: "100%", sm: labelWidth }}>
      <SoftTypography variant="button" fontWeight="medium" color="text">
        {label}
      </SoftTypography>
    </SoftBox>
    <SoftBox flex={1} width="100%">
      {element}
    </SoftBox>
  </Stack>
);

const ColorPickerField = ({ label, field, color, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [localColor, setLocalColor] = useState(color);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleColorChange = (newColor) => {
    setLocalColor(newColor.hex);
    onChange(field, newColor);
  };

  const open = Boolean(anchorEl);

  // Update local color when prop changes
  useEffect(() => {
    setLocalColor(color);
  }, [color]);

  return (
    <Grid item xs={12} sm={6} lg={4}>
      <SoftTypography variant="caption" fontWeight="medium" color="text" gutterBottom>
        {label}
      </SoftTypography>
      <Stack direction="row" alignItems="center" spacing={1} mt={0.5}>
        <SoftBox
          sx={{
            width: 60,
            height: 36,
            backgroundColor: localColor,
            border: "2px solid",
            borderColor: "divider",
            borderRadius: "8px",
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              borderColor: "#219197",
              transform: "scale(1.05)",
            },
          }}
          onClick={handleClick}
        />
        <SoftBox flex={1}>
          <StyledInput
            value={localColor}
            onChange={(e) => {
              const newColor = e.target.value;
              setLocalColor(newColor);
              onChange(field, { hex: newColor });
            }}
            startAdornment="#"
          />
        </SoftBox>
      </Stack>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          }
        }}
        sx={{ zIndex: 9999 }}
      >
        <SketchPicker 
          color={localColor} 
          onChange={handleColorChange}
          onChangeComplete={handleColorChange}
        />
      </Popover>
    </Grid>
  );
};

const Appearance = () => {
  const [themeMode, setThemeMode] = useState("");
  const [appearance, setAppearance] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [searchIconOption, setSearchIconOption] = useState("option1");
  const [showNotification, setShowNotification] = useState(false);
  const [customColors, setCustomColors] = useState({
    bg: "#ffffff",
    text: "#000000",
    buttonBg: "#1976d2",
    buttonHover: "#115293",
    inputBorder: "#cccccc",
    answerbgcolor: "#fdfdfd",
    tilesbg: "#f5f5f5",
    tilestextcolor: "#333333",
  });
  const [chatBotColors, setChatBotColors] = useState({
    bubbleBackground: "#219197",
    paneBackground: "#f9f9f9",
    questionBackground: "#ffffff",
    questionText: "#010103ff",
    hoverBackground: "#43d7dfff"
  });
  const [source, setSource] = useState("");
  const [form, setForm] = useState({
    iconHeight: "",
    iconWidth: "",
    defaultSearchText: "",
    unresolvedMessage: "",
    contactLabel: "",
    contactURL: "",
    reviewMessage: "",
    emailText: "",
    headertext: "",
  });
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);
  const [refreshTimer, setRefreshTimer] = useState(null);

  const { settings, updateSettings, loading: settingsLoading, refreshSettings } = useSettings();

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(muiTheme.breakpoints.down("md"));

  useEffect(() => {
    if (!settings && !settingsLoading) {
      if (refreshTimer) {
        clearInterval(refreshTimer);
      }

      const timer = setInterval(async () => {
        setRefreshing(true);
        try {
          await refreshSettings();
          setRefreshCount(prev => prev + 1);
        } catch (error) {
          console.error("Auto-refresh failed:", error);
        } finally {
          setRefreshing(false);
        }
      }, 20000);

      setRefreshTimer(timer);

      const initialRefresh = async () => {
        setRefreshing(true);
        try {
          await refreshSettings();
        } catch (error) {
          console.error("Initial refresh failed:", error);
        } finally {
          setRefreshing(false);
        }
      };
      initialRefresh();

      return () => {
        if (timer) {
          clearInterval(timer);
        }
      };
    } else if (settings && refreshTimer) {
      clearInterval(refreshTimer);
      setRefreshTimer(null);
    }
  }, [settings, settingsLoading, refreshCount]);

  useEffect(() => {
    const storedSource = localStorage.getItem("source");
    setSource(storedSource);
    if (storedSource === "webflow") {
      setShowNotification(true);
    }
  }, []);

  useEffect(() => {
    if (settings) {
      setForm({
        iconHeight: settings.ansera_search_logo_height || "",
        iconWidth: settings.ansera_search_logo_width || "",
        defaultSearchText: settings.ansera_search_search_bar_text || "",
        unresolvedMessage: settings.no_answer_text || "",
        contactLabel: settings.ansera_search_contact_us_text || "",
        contactURL: settings.ansera_search_contact_us_url || "",
        reviewMessage: settings.ansera_search_feedback_message || "",
        emailText: settings.ansera_search_send_mail_text || "",
        headertext: settings.ansera_chat_header_text || "",
      });

      setCustomColors({
        bg: settings.ansera_search_custom_bg_color || "#ffffff",
        text: settings.ansera_search_custom_text_color || "#000000",
        buttonBg: settings.ansera_search_custom_button_bg_color || "#1976d2",
        buttonHover: settings.ansera_search_custom_button_hover_color || "#115293",
        inputBorder: settings.ansera_search_custom_input_border_color || "#cccccc",
        answerbgcolor: settings.ansera_search_answer_bg_color || "#fdfdfd",
        tilesbg: settings.ansera_search_tiles_bg_color || "#f5f5f5",
        tilestextcolor: settings.ansera_search_tiles_text_color || "#333333",
      });

      setChatBotColors({
        bubbleBackground: settings.ansera_chat_bubble_background_color || "#219197",
        paneBackground: settings.ansera_chat_pane_background_color || "#f9f9f9",
        questionBackground: settings.ansera_chat_pane_question_background_color || "#ffffff",
        questionText: settings.ansera_chat_pane_question_text_color || "#010103ff",
        hoverBackground: settings.ansera_chat_bubble_hover_background_color || "#30b9c0ff"
      });

      setAppearance(settings.ansera_search_option3_widget || "search-bar");
      setThemeMode(settings.ansera_search_theme || "light");
      setSearchIconOption(
        settings.ansera_search_logo_url ||
        "https://www.cloudtern.com/wp-content/plugins/ansera-search/images/Ansera-Logo-Teal.PNG"
      );
    }
  }, [settings]);

  const handleColorChange = (field, color) => {
    setCustomColors((prev) => ({
      ...prev,
      [field]: color.hex,
    }));
  };

  const handleChatBotColorChange = (field, color) => {
    setChatBotColors((prev) => ({
      ...prev,
      [field]: color.hex,
    }));
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    try {
      if (!settings) return;
      
      setLoading(true);
      
      const updates = {};

      if (appearance !== settings.ansera_search_option3_widget)
        updates.ansera_search_option3_widget = appearance;

      if (themeMode !== settings.ansera_search_theme)
        updates.ansera_search_theme = themeMode;

      if (searchIconOption !== settings.ansera_search_logo_url)
        updates.ansera_search_logo_url = searchIconOption;

      if (form.iconHeight !== settings.ansera_search_logo_height)
        updates.ansera_search_logo_height = form.iconHeight;

      if (form.iconWidth !== settings.ansera_search_logo_width)
        updates.ansera_search_logo_width = form.iconWidth;

      if (form.defaultSearchText !== settings.ansera_search_search_bar_text)
        updates.ansera_search_search_bar_text = form.defaultSearchText;

      if (form.unresolvedMessage !== settings.no_answer_text)
        updates.no_answer_text = form.unresolvedMessage;

      if (form.contactLabel !== settings.ansera_search_contact_us_text)
        updates.ansera_search_contact_us_text = form.contactLabel;

      if (form.contactURL !== settings.ansera_search_contact_us_url)
        updates.ansera_search_contact_us_url = form.contactURL;

      if (form.reviewMessage !== settings.ansera_search_feedback_message)
        updates.ansera_search_feedback_message = form.reviewMessage;

      if (form.emailText !== settings.ansera_search_send_mail_text)
        updates.ansera_search_send_mail_text = form.emailText;

      if (form.headertext !== settings.ansera_chat_header_text)
        updates.ansera_chat_header_text = form.headertext;

      if (appearance === "chat-bot") {
        if (chatBotColors.bubbleBackground !== settings.ansera_chat_bubble_background_color)
          updates.ansera_chat_bubble_background_color = chatBotColors.bubbleBackground;
        if (chatBotColors.paneBackground !== settings.ansera_chat_pane_background_color)
          updates.ansera_chat_pane_background_color = chatBotColors.paneBackground;
        if (chatBotColors.questionBackground !== settings.ansera_chat_pane_question_background_color)
          updates.ansera_chat_pane_question_background_color = chatBotColors.questionBackground;
        if (chatBotColors.questionText !== settings.ansera_chat_pane_question_text_color)
          updates.ansera_chat_pane_question_text_color = chatBotColors.questionText;
        if(chatBotColors.hoverBackground !== settings.ansera_chat_bubble_hover_background_color)
          updates.ansera_chat_bubble_hover_background_color= chatBotColors.hoverBackground
      }

      if (themeMode === "custom") {
        if (customColors.bg !== settings.ansera_search_custom_bg_color)
          updates.ansera_search_custom_bg_color = customColors.bg;
        if (customColors.text !== settings.ansera_search_custom_text_color)
          updates.ansera_search_custom_text_color = customColors.text;
        if (customColors.buttonBg !== settings.ansera_search_custom_button_bg_color)
          updates.ansera_search_custom_button_bg_color = customColors.buttonBg;
        if (customColors.buttonHover !== settings.ansera_search_custom_button_hover_color)
          updates.ansera_search_custom_button_hover_color = customColors.buttonHover;
        if (customColors.inputBorder !== settings.ansera_search_custom_input_border_color)
          updates.ansera_search_custom_input_border_color = customColors.inputBorder;
        if (customColors.answerbgcolor !== settings.ansera_search_answer_bg_color)
          updates.ansera_search_answer_bg_color = customColors.answerbgcolor;
        if (customColors.tilesbg !== settings.ansera_search_tiles_bg_color)
          updates.ansera_search_tiles_bg_color = customColors.tilesbg;
        if (customColors.tilestextcolor !== settings.ansera_search_tiles_text_color)
          updates.ansera_search_tiles_text_color = customColors.tilestextcolor;
      }

      if (Object.keys(updates).length === 0) return;

      await updateSettings(updates);
      
      setSnackbarMessage("Settings updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to save settings:", error);
      setSnackbarMessage("Failed to save Settings!");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  if ((settingsLoading && !settings) || (!settings && refreshing)) {
    return (
      <SoftBox
        sx={{
          backgroundColor: "white",
          borderRadius: "16px",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
          p: 4,
        }}
      >
        <LoadingState />
        {refreshing && (
          <RefreshingLoader 
            message={``}
          />
        )}
      </SoftBox>
    );
  }

  const showRefreshingIndicator = refreshing && settings;

  return (
    <SoftBox
      sx={{
        backgroundColor: "white",
        borderRadius: "16px",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
        overflow: "hidden",
      }}
    >
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={(props) => <Slide {...props} direction="down" />}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: "8px",
            fontWeight: 500,
            color:"#ffffff",
            backgroundColor:
              snackbarSeverity === "success"
                ? "#008080"
                : snackbarSeverity === "error"
                ? "#d32f2f"
                : undefined,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      
      {showRefreshingIndicator && (
        <RefreshingLoader 
          message={`Refreshing settings... (Attempt ${refreshCount + 1})`}
        />
      )}
      
      {!settings ? (
        <SoftBox sx={{ p: 4, textAlign: "center" }}>
          <LoadingState />
          <RefreshingLoader 
            message={``}
          />
        </SoftBox>
      ) : (
        <>
          <SoftBox
            sx={{
              p: { xs: 2, sm: 3 },
              borderBottom: "1px solid",
              borderColor: "divider",
              backgroundColor: "white",
            }}
          >
            <NotificationBanner show={showNotification} onClose={() => setShowNotification(false)} />
            
            <Stack direction="row" alignItems="center" spacing={2}>
              <img src={AnseraLogo} alt="Ansera Logo" width={64} />
              <Box>
                <SoftTypography variant="h5" fontWeight="bold" color="text">
                  Appearance Settings
                </SoftTypography>
                <SoftTypography variant="body2" color="text.secondary">
                  Customize how users interact with Ansera
                </SoftTypography>
              </Box>
            </Stack>
          </SoftBox>

          <SoftBox sx={{ p: { xs: 2, sm: 3 } }}>
            <Card sx={{ p: 3, mb: 3, borderRadius: "12px" }}>
              <SectionHeader
                title="Theme Settings"
                description="Choose the visual theme for your widget"
              />
              
              <SettingsRow
                label="Plugin Theme"
                isMobile={isMobile}
                element={
                  <RadioGroup
                    row={!isMobile}
                    value={themeMode}
                    onChange={(e) => setThemeMode(e.target.value)}
                    name="pluginTheme"
                    sx={{ gap: isMobile ? 1 : 2 }}
                  >
                    <FormControlLabel
                      value="light"
                      control={<Radio size="small" />}
                      label="Light"
                    />
                    <FormControlLabel
                      value="dark"
                      control={<Radio size="small" />}
                      label="Dark"
                    />
                    <FormControlLabel
                      value="custom"
                      control={<Radio size="small" />}
                      label="Custom"
                    />
                  </RadioGroup>
                }
              />

              {themeMode === "custom" && (
                <SettingsRow
                  label="Custom Colors"
                  isMobile={isMobile}
                  element={
                    <Grid container spacing={2}>
                      <ColorPickerField
                        label="Background Color"
                        field="bg"
                        color={customColors.bg}
                        onChange={handleColorChange}
                      />
                      <ColorPickerField
                        label="Text Color"
                        field="text"
                        color={customColors.text}
                        onChange={handleColorChange}
                      />
                      <ColorPickerField
                        label="Button Background Color"
                        field="buttonBg"
                        color={customColors.buttonBg}
                        onChange={handleColorChange}
                      />
                      <ColorPickerField
                        label="Button Color on Hover"
                        field="buttonHover"
                        color={customColors.buttonHover}
                        onChange={handleColorChange}
                      />
                      <ColorPickerField
                        label="Text Input Box Border"
                        field="inputBorder"
                        color={customColors.inputBorder}
                        onChange={handleColorChange}
                      />
                      <ColorPickerField
                        label="Answer Area Background Color"
                        field="answerbgcolor"
                        color={customColors.answerbgcolor}
                        onChange={handleColorChange}
                      />
                    </Grid>
                  }
                />
              )}
            </Card>

            <Card sx={{ p: 3, mb: 3, borderRadius: "12px" }}>
              <SectionHeader
                title="Widget Settings"
                description="Configure how the widget appears on your site"
              />

              <SettingsRow
                label="Widget Appearance"
                isMobile={isMobile}
                element={
                  <RadioGroup
                    row={!isMobile}
                    value={appearance}
                    onChange={(e) => setAppearance(e.target.value)}
                    name="appearance"
                    sx={{ gap: isMobile ? 1 : 2 }}
                  >
                    <FormControlLabel
                      value="chat-bot"
                      control={<Radio size="small" />}
                      label="Chatbot"
                    />
                    <FormControlLabel
                      value="type-search"
                      control={<Radio size="small" />}
                      label="Search Bar"
                    />
                    {source === "wordpress" && (
                      <FormControlLabel
                        value="search-icon"
                        control={<Radio size="small" />}
                        label="Search Icon"
                      />
                    )}
                  </RadioGroup>
                }
              />
              
              {appearance === "chat-bot" && (
                <Card sx={{ p: 3, mb: 3, borderRadius: "12px" }}>
                  <SectionHeader
                    title="Chat Bot Colors"
                    description="Customize the appearance of your chat bot"
                  />
                  <Grid container spacing={2}>
                    <ColorPickerField
                      label="Chat Widget Bubble Background"
                      field="bubbleBackground"
                      color={chatBotColors.bubbleBackground}
                      onChange={handleChatBotColorChange}
                    />
                    <ColorPickerField
                      label="Chat Widget Bubble MouseOver Background Color"
                      field="hoverBackground"
                      color={chatBotColors.hoverBackground}
                      onChange={handleChatBotColorChange}
                    />
                    <ColorPickerField
                      label="Chat Widget Initial Pane Background Color"
                      field="paneBackground"
                      color={chatBotColors.paneBackground}
                      onChange={handleChatBotColorChange}
                    />
                    <ColorPickerField
                      label="Chat Widget Pane Question Background Color"
                      field="questionBackground"
                      color={chatBotColors.questionBackground}
                      onChange={handleChatBotColorChange}
                    />
                    <ColorPickerField
                      label="Chat Widget Pane Question Text Color"
                      field="questionText"
                      color={chatBotColors.questionText}
                      onChange={handleChatBotColorChange}
                    />
                  </Grid>
                </Card>
              )}

              <SettingsRow
                label="Search Bar Icon"
                isMobile={isMobile}
                element={
                  <RadioGroup
                    row={!isMobile}
                    value={searchIconOption}
                    onChange={(e) => setSearchIconOption(e.target.value)}
                    sx={{ gap: 2 }}
                  >
                    {[
                      {
                        value: "https://www.cloudtern.com/wp-content/plugins/ansera-search/images/Ansera-Logo-Gray.PNG",
                        label: "Gray",
                        bg: "transparent",
                      },
                      {
                        value: "https://www.cloudtern.com/wp-content/plugins/ansera-search/images/Ansera-Logo-White.PNG",
                        label: "White",
                        bg: "#000000",
                      },
                      {
                        value: "https://www.cloudtern.com/wp-content/plugins/ansera-search/images/Ansera-Logo-Teal.PNG",
                        label: "Teal",
                        bg: "transparent",
                      },
                    ].map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio />}
                        label={
                          <SoftBox
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              p: 1,
                              borderRadius: "8px",
                              backgroundColor: option.bg,
                              border: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <img
                              src={option.value}
                              alt={option.label}
                              style={{ width: 20, height: 20 }}
                            />
                            <SoftTypography variant="caption" color={option.bg ? "white" : "text"}>
                            </SoftTypography>
                          </SoftBox>
                        }
                      />
                    ))}
                  </RadioGroup>
                }
              />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <SettingsRow
                    label="Icon Height"
                    isMobile={isMobile}
                    element={
                      <StyledInput
                        name="iconHeight"
                        type="number"
                        value={form.iconHeight}
                        onChange={handleChange}
                        placeholder="Enter height"
                        endAdornment="px"
                      />
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SettingsRow
                    label="Icon Width"
                    isMobile={isMobile}
                    element={
                      <StyledInput
                        name="iconWidth"
                        type="number"
                        value={form.iconWidth}
                        onChange={handleChange}
                        placeholder="Enter width"
                        endAdornment="px"
                      />
                    }
                  />
                </Grid>
              </Grid>
            </Card>

            <Card sx={{ p: 3, borderRadius: "12px" }}>
              <SectionHeader
                title="Text & Content"
                description="Customize the text content throughout the widget"
              />

              {[
                { label: "Chat Widget Header Help Text", name: "headertext", placeholder: "Enter header help text" },
                { label: "Default Search Bar Text", name: "defaultSearchText", placeholder: "Enter default search text" },
                { label: "Message when AI cannot resolve", name: "unresolvedMessage", placeholder: "Enter unanswered query response" },
                { label: "Contact Page Label", name: "contactLabel", placeholder: "Landing page button label" },
                { label: "Contact Page URL", name: "contactURL", placeholder: "Enter landing page button URL" },
                { label: "Feedback Review Message", name: "reviewMessage", placeholder: "Enter feedback message" },
                { label: "Send Chat to Email Text", name: "emailText", placeholder: "Enter email button text" },
              ].map((field) => (
                <SettingsRow
                  key={field.name}
                  label={field.label}
                  isMobile={isMobile}
                  element={
                    <StyledInput
                      name={field.name}
                      value={form[field.name]}
                      onChange={handleChange}
                      placeholder={field.placeholder}
                    />
                  }
                />
              ))}
            </Card>

            <SoftBox sx={{ mt: 4, textAlign: { xs: "center", sm: "left" } }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSave}
                disabled={loading || settingsLoading || refreshing}
                sx={{
                  borderRadius: "8px",
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  minWidth: { xs: "100%", sm: "auto" },
                  backgroundColor: "#219297",
                  color: "#fff",
                  textTransform: "none",
                  boxShadow: "0 4px 6px rgba(33, 146, 151, 0.2)",
                  "&:hover": { 
                    backgroundColor: "#1a767a",
                    boxShadow: "0 6px 12px rgba(33, 146, 151, 0.3)"
                  },
                  "&:active": { 
                    backgroundColor: "#1a767a",
                    transform: "translateY(1px)"
                  },
                  "&:focus": { 
                    backgroundColor: "#219297",
                    boxShadow: "0 0 0 3px rgba(33, 146, 151, 0.2)"
                  }
                }}
              >
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </SoftBox>
          </SoftBox>
        </>
      )}
    </SoftBox>
  );
};

export default Appearance;